import { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../../state/worldStore';
import { useMoment, momentPose, momentFail, momentBridge, MOMENT_ID } from '../../city/moment';
import { geoReady } from '../../city/cityMotion';
import { pathsReady } from '../../city/cityPath';
import { readyBodies, readyAt } from '../../city/bodies';
import { sightTestFor } from '../../city/momentSight';
import { bootState, pendingLoads } from '../../state/bootProgress';
import { introBridge } from '../../state/introBridge';

// MomentDirector — the camera and the go-signal for a moment's still (city/moment);
// loading, staging and the watchdog live outside the canvas (MomentBoot).
//
// Inside the canvas, mounted LAST so its camera write is the last one each frame
// (the walk and the cutscene director stand down for `cutsceneBridge.active`).
// It holds the camera on the frame, and says `window.__shotReady` only when
// everything in the frame is real: the city drawn, the places and road map in (or the spots would sit at a
// fallback point), both bodies loaded (not the grey stand-ins), and nothing
// still downloading — then a dozen steady frames, so the shot is not caught
// mid-settle. Anything that cannot happen in time says so (`__shotError`).
const STEADY_FRAMES = 12;
const QUIET_MS = 4000;           // a load that never finishes does not hold the shot past this, once all else is in
// A body counts this long after it is in the scene (2026-11-01): "ready" is the
// mesh arriving, and its idle clip then fades in over 0.25 s — a dozen frames on
// a fast GPU is 0.2 s, and the first real still caught one arms-out mid-fade.
const BODY_SETTLE_MS = 1500;

export default function MomentDirector() {
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const npcList = useWorldStore((s) => s.npcList);
  const scene = useThree((s) => s.scene);
  const pose = useRef(null);
  const lastKey = useRef('');
  // THE CHECKED FRAME (city/momentSight): planned once with real sight lines,
  // when the city, the places and both bodies are in; until then the plain plan
  // (from the road map alone) holds the camera roughly right while things load.
  const sight = useRef(null);
  const checked = useRef(null);   // { base: the plain plan's key, pose }
  const allInAt = useRef(0);
  const noBodySince = useRef(0);

  useFrame(() => {
    if (!MOMENT_ID) return;
    const st = useMoment.getState().state;
    if (st !== 'staged' && st !== 'shot') return;
    // Re-plan until the ground under them is final, then hold.
    const aspect = size.width / Math.max(1, size.height);
    let p = momentPose(aspect);
    const { moment: m } = useMoment.getState();
    const ids = [Number(m.a.id), Number(m.b.id)];
    const settled = ids.every((id) => readyBodies.has(id) && performance.now() - (readyAt.get(id) ?? Infinity) >= BODY_SETTLE_MS);
    const sceneIn = bootState().marks.has('frame') && geoReady() && !!venueAnchors && pathsReady() && settled;
    if (p && sceneIn) {
      const base = [...p.pos, ...p.look].map((v) => v.toFixed(3)).join(',');
      if (checked.current?.base !== base) {
        if (!sight.current) sight.current = sightTestFor(scene);
        const t0 = performance.now();
        const c = momentPose(aspect, Date.now(), sight.current);
        checked.current = { base, pose: c };
        console.log(`[moment] framed with sight lines in ${Math.round(performance.now() - t0)} ms (${sight.current.count()} rays so far): ${c?.clear ? 'clear' : 'no clear angle — the best of the rest'}, ×${(c?.k ?? 1).toFixed(2)} of the planned distance`);
      }
      p = checked.current.pose || p;
    }
    if (p) {
      const key = [...p.pos, ...p.look].map((v) => v.toFixed(3)).join(',');
      if (key !== lastKey.current) { lastKey.current = key; momentBridge.steady = 0; }
      pose.current = p;
    }
    if (pose.current && !introBridge.active) {
      const { pos, look, fov } = pose.current;
      camera.position.set(pos[0], pos[1], pos[2]);
      camera.lookAt(look[0], look[1], look[2]);
      if (camera.isPerspectiveCamera && fov && Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }
    }
    if (st === 'shot') return;   // the camera stays held while the picture is taken

    const rigged = (id) => (npcList || []).some((n) => Number(n.avatarId) === id && n.rigged && !n.test);
    // Someone with no 3D body at all: say so, rather than wait for one that is not coming.
    if (npcList?.length) {
      const none = ids.filter((id) => !rigged(id));
      if (none.length) {
        if (!noBodySince.current) noBodySince.current = performance.now();
        if (performance.now() - noBodySince.current > 15000) { momentFail(`no 3D body for ${none.map((id) => (id === ids[0] ? m.a.name : m.b.name)).join(' and ')}`); return; }
      } else noBodySince.current = 0;
    }
    const checks = {
      city: bootState().marks.has('frame'),
      places: geoReady() && !!venueAnchors && pathsReady(),
      framed: !!pose.current && !introBridge.active && !!checked.current,
      bodies: settled,
    };
    momentBridge.checks = checks;
    if (!Object.values(checks).every(Boolean)) { allInAt.current = 0; momentBridge.steady = 0; return; }
    if (!allInAt.current) allInAt.current = performance.now();
    const quiet = pendingLoads().length === 0 || performance.now() - allInAt.current > QUIET_MS;
    if (!quiet) { momentBridge.steady = 0; return; }
    momentBridge.steady += 1;
    if (momentBridge.steady >= STEADY_FRAMES) {
      useMoment.setState({ state: 'shot' });
      window.__shotReady = true;
      console.log(`[moment] ready at ${Math.round(performance.now() / 1000)}s`);
    }
  });

  return null;
}
